<template>
  <div id="degree-equivalency" class="container px-lg-space-xxxl">
    <div class="row">
      <div class="col-12">
        <h2 class="h2-medium mb-space-md">{{ title }}</h2>
      </div>
    </div>
    <!-- v-selects -->
    <div class="row">
      <!-- Application Level -->
      <div class="col-12">
        <div class="col-md-8 col-lg-4">
          <label for="application-level-type_typeahead__search" class="fw-bold mb-space-xxs"
            >Application Level</label
          >
          <client-only>
            <type-ahead-select
              id="application-level-type"
              v-model="selectedApplicationLevel"
              :options="applicationLevelOptions"
              placeholder="Select type of degree you're applying to"
              :disabled="responseError"
              :clearable="false"
              class="fw-normal text-dark-1 mb-space-sm vue-select"
              @changed="handleApplicationLevelChange"
            >
            </type-ahead-select>
          </client-only>
        </div>
      </div>
      <!-- Country -->
      <div class="col-12">
        <div class="col-md-8 col-lg-4">
          <label class="fw-bold mb-space-xxs" for="country_typeahead__search">Country</label>
          <client-only>
            <type-ahead-select
              id="country"
              v-model="selectedCountry"
              :options="countryOptions as CountrySelect[]"
              placeholder="Select your country"
              :clearable="false"
              :disabled="!selectedApplicationLevel"
              class="fw-normal text-dark-1 mb-space-sm vue-select"
              @input="handleCountryChange"
            >
            </type-ahead-select>
          </client-only>
        </div>
      </div>
      <!-- form submit -->
      <div class="col-12">
        <button
          :disabled="!selectedApplicationLevel || !selectedCountry"
          class="btn btn-secondary"
          @click="handleEquivalencyClick()"
        >
          See equivalency
        </button>
      </div>
      <div class="text-xs mt-space-xs d-block d-md-none">
        <p>Swipe down to see results after you click “See equivalency”</p>
      </div>
    </div>
    <!-- equivalency fetch error -->
    <div v-if="responseError" class="row">
      <div class="col-12">
        <div class="alert alert-danger" role="alert">
          Oops! We are currently experiencing issues retrieving degree information. We apologize for
          the inconvenience, please try back later.
        </div>
      </div>
    </div>
    <!-- equivalency result -->
    <aside v-else class="row">
      <transition name="fade">
        <div v-if="!isEquivalencyShown" class="col-12 position-relative">
          <img
            src="https://cms.asuonline.asu.edu/sites/default/files/degree%20equivalency%20placeholder.jpg"
            class="img-fluid d-none d-md-block"
            alt="plug your information above and choose 'see equivalency'"
          />
          <img
            src="https://cms.asuonline.asu.edu/sites/default/files/degree-equivalency-placeholder-mobile.jpg"
            class="img-fluid d-block d-lg-none"
            alt="plug your information above and choose 'see equivalency'"
          />
          <div class="position-absolute calc-text">
            <p class="fs-xl fw-bold position-relative text-center">
              Plug in your information above and choose ‘See equivalency’.
            </p>
          </div>
        </div>
        <div v-else class="bg-light-2 p-space-md p-md-space-lg mt-space-xl equivalency-container">
          <!-- selectedApplicationLevel === 'Undergraduate' -->
          <template v-if="selectedApplicationLevel?.value === DegreeType.UGText">
            <h3 class="h3-xl mb-space-sm">{{ equivalencyUg?.Country }}</h3>
            <p class="mb-space-xs fw-bold">
              {{ equivalencyUg?.NameOfFinalCertificateOrDiplomaLabel }}
            </p>
            <p class="mb-space-md" v-html="equivalencyUg?.NameofFinalCertificateorDiploma"></p>
            <p class="mb-space-xs fw-bold">
              {{ equivalencyUg?.MinimumOverallGradeRequirementLabel }}
            </p>
            <p class="mb-space-md" v-html="equivalencyUg?.MinimumOverallGradeRequirement"></p>
            <p class="mb-space-xs fw-bold">
              {{ equivalencyUg?.EnglishProficiencyRequiredLabel }}
            </p>
            <nuxt-link target="_blank" :to="equivalencyUg?.EnglishProficiencyRequiredUrl">{{
              equivalencyUg?.EnglishProficiencyRequired
            }}</nuxt-link>
          </template>
          <template v-else>
            <h3 class="h3-xl mb-space-sm">{{ equivalencyGrad?.Country }}</h3>
            <p class="mb-space-xs fw-bold">
              {{ equivalencyGrad?.BachelorsEquivalentLabel }}
            </p>
            <p class="mb-space-md" v-html="equivalencyGrad?.BachelorsEquivalent"></p>
            <p class="mb-space-xs fw-bold">
              {{ equivalencyGrad?.DegreeCertLabel }}
            </p>
            <p class="mb-space-md" v-html="equivalencyGrad?.DegreeLengthLabel"></p>
            <p class="mb-space-xs fw-bold">
              {{ equivalencyGrad?.DegreeLengthLabel }}
            </p>
            <p class="mb-space-md" v-html="equivalencyGrad?.DegreeLength"></p>
            <p class="mb-space-xs fw-bold">
              {{ equivalencyGrad?.DiplomaSupplementAvailableLabel }}
            </p>
            <nuxt-link target="_blank" :to="equivalencyGrad?.EnglishProficiencyrequiredUrl">{{
              equivalencyGrad?.EnglishProficiencyRequired
            }}</nuxt-link>
            <p>
              {{ equivalencyGrad?.EnglishTranslationRequiredLabel }}
            </p>
            <p>
              {{ equivalencyGrad?.EnglishTranslationRequired }}
            </p>
            <p>
              {{ equivalencyGrad?.TranscriptsIssuedInEnglishLabel }}
            </p>
            <p>
              {{ equivalencyGrad?.TranscriptsIssuedInEnglish }}
            </p>
          </template>
          <div
            class="pt-space-sm mt-space-sm equivalency-note"
            v-html="equivalencyDisclaimer"
          ></div>
        </div>
      </transition>
    </aside>
  </div>
</template>
<script lang="ts" setup>
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import TypeAheadSelect from "../TypeAheadSelect.vue";
const runtimeConfig = useRuntimeConfig();

const EQUIVALENCY_API_RETRIES = 2;
const EQUIVALENCY_API_RETRY_DELAY_MS = 1000;
const EQUIVALENCY_API_TIMEOUT_MS = 10000;

enum DegreeType {
  UGText = "undergraduate",
  GradText = "graduate"
}
type ApplicationLevel = {
  text: string;
  value: string;
};

type CountrySelect = {
  text: string;
  value: string;
};

type CountryInfo = {
  countryLabel: string;
  countryId: string;
};

type EducationalRequirementUG = {
  Country: string;
  NameofFinalCertificateorDiploma: string;
  NameOfFinalCertificateOrDiplomaLabel: string;
  MinimumOverallGradeRequirement: string;
  MinimumOverallGradeRequirementLabel: string;
  EnglishProficiencyRequired: string;
  EnglishProficiencyRequiredLabel: string;
  EnglishProficiencyRequiredUrl: string;
  Grade: string;
};

type EducationalRequirementGrad = {
  Country: string;
  BachelorsEquivalentLabel: string;
  BachelorsEquivalent: string;
  DegreeCertLabel: string;
  DegreeLengthLabel: string;
  DegreeLength: string;
  DiplomaSupplementAvailableLabel: string;
  DiplomaSupplementAvailable: string;
  EnglishProficiencyRequiredLabel: string;
  EnglishProficiencyrequiredUrl: string;
  EnglishProficiencyRequired: string;
  EnglishTranslationRequiredLabel: string;
  EnglishTranslationRequired: string;
  TranscriptsIssuedInEnglishLabel: string;
  TranscriptsIssuedInEnglish: string;
};

type UgResponse = {
  byCountry: {
    [country: string]: EducationalRequirementUG;
  };
  countries: CountryInfo[];
};

type GradResponse = {
  byCountry: {
    [country: string]: EducationalRequirementGrad;
  };
  countries: CountryInfo[];
};

/** The equivalency API wraps every payload in a `data` envelope. */
type EquivalencyApiResponse<T> = {
  data: T;
};

/** Country requirements come back keyed by country, alongside a `countries` list. */
type EquivalencyByCountryPayload = {
  countries: CountryInfo[];
} & Record<string, unknown>;
/*
 ********************************************
 * Data
 ********************************************
 */
const responseError = ref(false);
const equivalencyUg = ref<EducationalRequirementUG | null>(null);
const equivalencyGrad = ref<EducationalRequirementGrad | null>(null);
const isEquivalencyShown = ref(false);
const selectedApplicationLevel = ref<ApplicationLevel | null>(null);
const selectedCountry = ref<CountrySelect | null>(null);
const applicationLevelOptions = ref<ApplicationLevel[]>([]);
const ugResponse = ref<UgResponse | null>(null);
const gradResponse = ref<GradResponse | null>(null);
/*
 ********************************************
 * Props
 ********************************************
 */
type Props = {
  title: string;
  equivalencyDisclaimer: string;
};
defineProps<Props>();

/*
 ********************************************
 * Computed
 ********************************************
 */
const countryOptions = computed(() => {
  if (
    selectedApplicationLevel.value &&
    selectedApplicationLevel.value?.value === DegreeType.UGText
  ) {
    return ugResponse.value?.countries.map((country: CountryInfo) => {
      return {
        text: country.countryLabel,
        value: country.countryId
      };
    });
  } else if (
    selectedApplicationLevel.value &&
    selectedApplicationLevel.value?.value === DegreeType.GradText
  ) {
    return gradResponse.value?.countries?.map((country: CountryInfo) => {
      return {
        text: country.countryLabel,
        value: country.countryId
      };
    });
  }
  return [];
});

/*
 ********************************************
 * Methods
 ********************************************
 */
const handleEquivalencyClick = () => {
  isEquivalencyShown.value = true;
  if (
    selectedCountry.value &&
    selectedApplicationLevel.value &&
    selectedApplicationLevel.value.value === DegreeType.UGText
  ) {
    equivalencyUg.value = ugResponse.value?.byCountry[selectedCountry.value.value] || null;
  } else if (
    selectedCountry.value &&
    selectedApplicationLevel.value &&
    selectedApplicationLevel.value.value === DegreeType.GradText
  ) {
    equivalencyGrad.value = gradResponse.value?.byCountry[selectedCountry.value.value] || null;
  }
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    "pick your country and see equivalency",
    "see equivalency"
  );
};

const handleCountryChange = () => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "country",
    "main content",
    "pick your country and see equivalency",
    selectedCountry?.value?.value.toLocaleLowerCase() || "N/A"
  );
};

const handleApplicationLevelChange = () => {
  isEquivalencyShown.value = false;
  equivalencyUg.value = null;
  equivalencyGrad.value = null;
  selectedCountry.value = null;
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "application level",
    "main content",
    "pick your country and see equivalency",
    selectedApplicationLevel?.value?.value.toLocaleLowerCase() || "N/A"
  );
};

// `$fetch` resolves to an untyped global in this project, so the response shape
// is asserted here to keep the call sites type-safe.
const fetchEquivalencyPath = async <T,>(path: string): Promise<EquivalencyApiResponse<T>> => {
  const response = await $fetch(path, {
    baseURL: runtimeConfig.public.asuoApiBaseUrl,
    headers: {
      Accept: "application/json"
    },
    retry: EQUIVALENCY_API_RETRIES,
    retryDelay: EQUIVALENCY_API_RETRY_DELAY_MS,
    timeout: EQUIVALENCY_API_TIMEOUT_MS
  });

  return response as EquivalencyApiResponse<T>;
};

const fetchApplicationLevelOptions = async () => {
  try {
    const response = await fetchEquivalencyPath<ApplicationLevel[]>(
      runtimeConfig.public.NUXT_PUBLIC_ASUO_API_EQUIVALENCY_OPTIONS_PATH
    );
    applicationLevelOptions.value = response.data;
  } catch (error) {
    console.error("error", error);
    responseError.value = true;
  }
};

const fetchAdmissionsUg = async () => {
  try {
    const response = await fetchEquivalencyPath<EquivalencyByCountryPayload>(
      runtimeConfig.public.NUXT_PUBLIC_ASUO_API_EQUIVALENCY_UG_PATH
    );
    const { countries, ...byCountry } = response.data;
    ugResponse.value = toUGResponse({
      countries: countries,
      byCountry: byCountry
    });
  } catch (error) {
    console.error("error", error);
    responseError.value = true;
  }
};

const fetchAdmissionsGrad = async () => {
  try {
    const response = await fetchEquivalencyPath<EquivalencyByCountryPayload>(
      runtimeConfig.public.NUXT_PUBLIC_ASUO_API_EQUIVALENCY_GR_PATH
    );
    const { countries, ...byCountry } = response.data;
    gradResponse.value = toGradResponse({
      countries: countries,
      byCountry: byCountry
    });
  } catch (error) {
    console.error("error", error);
    responseError.value = true;
  }
};
const toGradResponse = (response: Record<string, unknown>): GradResponse => {
  const countries = response.countries as CountryInfo[];
  const byCountry = response.byCountry as {
    [country: string]: EducationalRequirementGrad;
  };
  return {
    countries,
    byCountry
  };
};

const toUGResponse = (response: Record<string, unknown>): UgResponse => {
  const countries = response.countries as CountryInfo[];
  const byCountry = response.byCountry as {
    [country: string]: EducationalRequirementUG;
  };
  return {
    countries,
    byCountry
  };
};
/*
 ********************************************
 * hooks
 ********************************************
 */
onMounted(async () => {
  await Promise.all([fetchApplicationLevelOptions(), fetchAdmissionsUg(), fetchAdmissionsGrad()]);
});
</script>

<style lang="scss" scoped>
.equivalency-container {
  border: 1px solid var(--rds-light-4);
}
.equivalency-note {
  border-top: 1px solid var(--rds-light-4);
}

.calc-text {
  top: 250px;
  width: 100%;
}
@media (min-width: 992px) {
  .calc-text {
    width: 50%;
    top: 250px;
    left: 100px;
  }
}
</style>
